import React, { useEffect, useState } from "react";
import { Dropdown, Form, Modal, OverlayTrigger, Placeholder, Tooltip } from "react-bootstrap";

import DstAlert from "../../../../components/DstAlert/DstAlert";
import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import {
  calculateEndDate,
  daysMapping,
  formatToISODateTime,
  getLocalHourToUTC,
  isPast,
} from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import {
  Animator,
  LanguageKeys,
  MeetingCreationFormState,
  MeetingCreationModalProps,
  MeetingTypes,
} from "./CreationModal.d";
import {
  createMeeting,
  getMeetingTypes,
  getResources,
  getSuggestedNames,
  languageMapping,
  visibilityOptions,
} from "./CreationModal.function";

const initialForm: MeetingCreationFormState = {
  afternoonEnd: "",
  afternoonStart: "",
  description: "",
  duration: 120,
  durationError: false,
  endDate: "",
  meetingLinkService: "zoom",
  meetingLink: "",
  meetingType: null,
  morningEnd: "",
  morningStart: "",
  selectedAnimators: [],
  selectedDays: [],
  selectedLanguage: "fr-FR",
  selectedProgram: null,
  selectedProject: null,
  selectedVisibility: "private",
  startDate: "",
  targetCohortIds: [],
  title: "",
};

const MeetingCreationModal = ({ language, setShowCreationModal, showCreationModal }: MeetingCreationModalProps) => {
  const { email } = useSession();
  const [form, setForm] = useState<MeetingCreationFormState>(initialForm);
  const [resources, setResources] = useState<{ animators: Animator[]; meetingTypes: MeetingTypes[]; programs: any[] }>({
    animators: [],
    meetingTypes: [],
    programs: [],
  });

  const [loading, setLoading] = useState({
    types: true,
    resources: true,
    submit: false,
  });
  const [alert, setAlert] = useState({ show: false, props: {} as any });

  const _updateForm = (fields: Partial<MeetingCreationFormState>) => setForm((prev) => ({ ...prev, ...fields }));

  const displayAlert = (props: any) => setAlert({ show: true, props });
  const hideAlert = () => setAlert((prev) => ({ ...prev, show: false }));

  const _handleAlertClose = () => {
    hideAlert();
    resetSelection();
  };

  useEffect(() => {
    getMeetingTypes(
      (meetingTypes: MeetingTypes[]) => setResources((prev) => ({ ...prev, meetingTypes })),
      setLoading,
      language
    );
    getResources(
      (animators: Animator[]) => setResources((prev) => ({ ...prev, animators })),
      (programs: any[]) => setResources((prev) => ({ ...prev, programs })),
      setLoading,
      language
    );
  }, [language]);

  useEffect(() => {
    _updateForm({
      meetingType: resources.meetingTypes.find((type) => type.name.toLowerCase() === "masterclass") || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources.meetingTypes]);

  useEffect(() => {
    if (form.meetingType?.need_project) {
      const sessionUserAnimator = resources.animators.find((animator) => animator.email === email);
      _updateForm({
        duration: 60,
        selectedAnimators:
          sessionUserAnimator && form.selectedAnimators.length === 0 ? [sessionUserAnimator] : form.selectedAnimators,
      });
    } else if (form.meetingType?.name.toLowerCase() !== "multiple days") {
      _updateForm({ duration: 120 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.meetingType]);

  const getDaysOptions = (language: string) =>
    Object.entries(daysMapping(language))
      .filter(([_, value]) => value !== undefined)
      .map(([key, day]) => ({
        id: key,
        day: day!,
      }));

  const handleConfirm = () => {
    const startDate = new Date(form.startDate).toISOString();
    const optionalFields = {
      afternoon_end_date: form.afternoonEnd ? getLocalHourToUTC(form.afternoonEnd, startDate.split("T")[0]) : "",
      afternoon_start_date: form.afternoonStart ? getLocalHourToUTC(form.afternoonStart, startDate.split("T")[0]) : "",
      animator_ids:
        form.selectedAnimators.length > 0
          ? form.selectedAnimators.map((animator) => animator.staff_member_id)
          : undefined,
      description: form.description || "",
      url: form.meetingLink || "",
      morning_end_date: form.morningEnd ? getLocalHourToUTC(form.morningEnd, startDate.split("T")[0]) : "",
      morning_start_date: form.morningStart ? getLocalHourToUTC(form.morningStart, startDate.split("T")[0]) : "",
    };

    const payload = {
      cohort_ids: form.targetCohortIds,
      days:
        form.meetingType?.name.toLowerCase() === "multiple days" && form.selectedDays?.length > 0
          ? form.selectedDays.map((day) => day.id)
          : undefined,
      dt_end:
        form.meetingType?.name.toLowerCase() === "multiple days"
          ? new Date(form.endDate).toISOString()
          : calculateEndDate(form.startDate, form.duration),
      service_link_generation: form.meetingLinkService,
      language: form.selectedLanguage,
      program_id: form.selectedProgram?.id,
      name: form.title,
      type_id: form.meetingType?.id,
      project_id: form.meetingType?.need_project && form.selectedProject ? form.selectedProject.id : undefined,
      dt_start: startDate,
      visibility: form.selectedVisibility,
      ...optionalFields,
    };

    createMeeting(payload, language, setLoading, displayAlert, setShowCreationModal, resetSelection, hideAlert);
  };

  const resetSelection = () =>
    setForm({
      ...initialForm,
      meetingType: resources.meetingTypes.find((type) => type.name.toLowerCase() === "masterclass") || null,
    });

  const _handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseInt(event.target.value, 10);
    if (parsedValue > 0) {
      _updateForm({ duration: parsedValue, durationError: false });
    } else {
      _updateForm({ duration: 0, durationError: true });
    }
  };

  const isFormValid = (() => {
    if (
      !form.title ||
      !form.startDate ||
      isPast(form.startDate) ||
      !form.selectedLanguage ||
      (!form.meetingType?.need_project && !form.selectedProgram)
    ) {
      return false;
    }

    if (form.meetingType?.need_project) return !!form.selectedProject;

    switch (form.meetingType?.name.toLowerCase()) {
      case "multiple days": {
        const hasMorning = form.morningStart && form.morningEnd;
        const hasAfternoon = form.afternoonStart && form.afternoonEnd;

        if (!hasMorning && !hasAfternoon) return false;

        return !!form.endDate && form.selectedDays.length > 0 && (hasMorning || hasAfternoon);
      }

      default:
        return !!form.duration;
    }
  })();

  return (
    <React.Fragment>
      <Popup
        show={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        modalTitle={translate(language, "PAGES.MEETINGS.MODAL.TITLE")}
        scrollable
      >
        <Form id="modal-add-meeting">
          <Form.Group>
            <Form.Label>
              <RequiredLabel label={translate(language, "PAGES.MEETINGS.MODAL.LABELS.MEETING_TYPE")} />
            </Form.Label>
            {resources.meetingTypes.length > 0 ? (
              <Dropdown>
                <Dropdown.Toggle
                  className="w-100 d-flex justify-content-between align-items-center border-secondary"
                  variant=""
                >
                  {form.meetingType?.name}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {resources.meetingTypes.map((type) => (
                    <Dropdown.Item key={type.id} onClick={() => _updateForm({ meetingType: type })}>
                      {type.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Placeholder animation="glow">
                <Placeholder.Button className="w-100" />
              </Placeholder>
            )}
          </Form.Group>
          <React.Fragment>
            <Form.Group className="mt-3">
              <Form.Label>
                {!form.meetingType?.need_project ? (
                  <RequiredLabel label={translate(language, "PAGES.MEETINGS.MODAL.LABELS.PROGRAM")} />
                ) : (
                  translate(language, "PAGES.MEETINGS.MODAL.LABELS.PROGRAM")
                )}
              </Form.Label>
              <MultiSelectDropdown
                btnWidth="100%"
                label={form.selectedProgram?.name || translate(language, "PAGES.MEETINGS.MODAL.LABELS.PROGRAM")}
                items={resources.programs}
                onConfirm={(selectedItems) => _updateForm({ selectedProgram: selectedItems?.[0] || null })}
                badgeItemProperty="name"
                displayItem={(item) => item.name}
                language={language}
                toggleClass={`text-truncate border-secondary w-100 ${!form.selectedProgram ? "text-muted" : ""}`}
                isSingleSelect
                mock={!resources.programs.length}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
              </Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  className="w-100 d-flex justify-content-between align-items-center border-secondary"
                  variant=""
                >
                  {languageMapping(language)[form.selectedLanguage]}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {Object.keys(languageMapping(language)).map((key) => (
                    <Dropdown.Item key={key} onClick={() => _updateForm({ selectedLanguage: key as LanguageKeys })}>
                      {languageMapping(language)[key as LanguageKeys]}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          </React.Fragment>

          {form.meetingType?.need_project && (
            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "PAGES.MEETINGS.MODAL.LABELS.PROJECT")} />
              </Form.Label>
              <MultiSelectLiveDropdown
                label={
                  form.selectedProject
                    ? `${translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.PROJECT_SELECTED")} ${
                        form.selectedProject.name
                      }`
                    : translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.SELECT_PROJECT")
                }
                category={["projects"]}
                language={language}
                isSingleSelect
                onConfirm={(selected) =>
                  _updateForm({
                    selectedProject:
                      selected.length > 0 ? { id: +selected[0].value.id, name: selected[0].value.name } : null,
                  })
                }
                toggleClass={`text-truncate w-100 border-secondary ${!form.selectedProject ? "text-muted" : ""}`}
              />
            </Form.Group>
          )}
          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.TITLE")} />
            </Form.Label>
            <Form.Control
              className={`border-secondary ${!form.title ? "text-muted" : ""}`}
              type="text"
              placeholder={translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.TITLE_SELECT")}
              value={form.title}
              onChange={(event) => _updateForm({ title: event.target.value })}
              list="suggested-title-list"
            />
            <datalist id="suggested-title-list">
              {getSuggestedNames(form.meetingType!, form.selectedProgram?.id, form.selectedLanguage).map(
                (name, index) => (
                  <option key={index} value={name} />
                )
              )}
            </datalist>
          </Form.Group>

          {!form.meetingType?.need_project && (
            <Form.Group className="mt-3">
              <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.COHORTS")}</Form.Label>
              <MultiSelectLiveDropdown
                category={["cohorts"]}
                label={
                  form.targetCohortIds.length > 0
                    ? `${translate(
                        language,
                        "PAGES.MEETINGS.MODAL.PLACEHOLDERS.COHORT_SELECTED"
                      )} ${form.targetCohortIds.join(", ")}`
                    : translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.SELECT_COHORT")
                }
                language={language}
                onConfirm={(selectedCohorts) =>
                  _updateForm({ targetCohortIds: selectedCohorts.map((cohort) => parseInt(cohort.value.id, 10)) })
                }
                toggleClass={`text-truncate w-100 border-secondary ${
                  form.targetCohortIds.length === 0 ? "text-muted" : ""
                }`}
              />
            </Form.Group>
          )}

          <Form.Group className="mt-3">
            <Form.Label>
              <RequiredLabel label={translate(language, "GLOBAL.START_DATE")} />
            </Form.Label>
            <Form.Control
              className={`border-secondary ${!form.startDate ? "text-muted" : ""}`}
              type="datetime-local"
              min={formatToISODateTime(new Date().toISOString(), true).replace(" ", "T")}
              value={form.startDate}
              onChange={(event) => _updateForm({ startDate: event.target.value })}
            />
          </Form.Group>
          {form.meetingType?.name.toLowerCase() === "multiple days" ? (
            <React.Fragment>
              <Form.Group className="mt-3">
                <Form.Label>
                  <RequiredLabel label={translate(language, "GLOBAL.END_DATE")} />
                </Form.Label>
                <Form.Control
                  className={`border-secondary ${!form.endDate ? "text-muted" : ""}`}
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(event) => _updateForm({ endDate: event.target.value })}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>
                  {translate(language, "PAGES.MEETINGS.MODAL.LABELS.MORNING_START")}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="time-slots-tooltip">
                        {translate(language, "PAGES.MEETINGS.MODAL.LABELS.TIME_SLOTS_INFO")}
                      </Tooltip>
                    }
                  >
                    <span className="dst-icon-info-circle ms-1 text-muted clickable" />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  className={`border-secondary ${!form.morningStart ? "text-muted" : ""}`}
                  type="time"
                  value={form.morningStart}
                  onChange={(event) => _updateForm({ morningStart: event.target.value })}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.MORNING_END")}</Form.Label>
                <Form.Control
                  className={`border-secondary ${!form.morningEnd ? "text-muted" : ""}`}
                  type="time"
                  value={form.morningEnd}
                  onChange={(event) => _updateForm({ morningEnd: event.target.value })}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.AFTERNOON_START")}</Form.Label>
                <Form.Control
                  className={`border-secondary ${!form.afternoonStart ? "text-muted" : ""}`}
                  type="time"
                  value={form.afternoonStart}
                  onChange={(event) => _updateForm({ afternoonStart: event.target.value })}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.AFTERNOON_END")}</Form.Label>
                <Form.Control
                  className={`border-secondary ${!form.afternoonEnd ? "text-muted" : ""}`}
                  type="time"
                  value={form.afternoonEnd}
                  onChange={(event) => _updateForm({ afternoonEnd: event.target.value })}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>
                  <RequiredLabel label={translate(language, "PAGES.MEETINGS.MODAL.LABELS.DAYS")} />
                </Form.Label>
                <MultiSelectDropdown
                  label={
                    form.selectedDays.length > 0
                      ? form.selectedDays.map((item) => item.day).join(", ")
                      : translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.SELECT_DAYS")
                  }
                  items={getDaysOptions(language)}
                  onConfirm={(selectedDays) => _updateForm({ selectedDays })}
                  badgeItemProperty="day"
                  displayItem={(item) => item.day}
                  language={language}
                  toggleClass={`text-truncate w-100 border-secondary ${
                    form.selectedDays.length === 0 ? "text-muted" : ""
                  }`}
                />
              </Form.Group>
            </React.Fragment>
          ) : (
            <Form.Group className="mt-3">
              <Form.Label>
                <RequiredLabel label={translate(language, "GLOBAL.DURATION")} />
              </Form.Label>
              <Form.Control
                className={`border-secondary ${form.durationError ? "border-danger" : ""}`}
                type="text"
                value={form.duration}
                onChange={_handleDurationChange}
              />
              {form.durationError && (
                <Form.Text className="text-danger">
                  {translate(language, "PAGES.MEETINGS.MODAL.ERRORS.UNFILLED_DURATION")}
                </Form.Text>
              )}
            </Form.Group>
          )}

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.VISIBILITY")}</Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                className="w-100 d-flex justify-content-between align-items-center border-secondary"
                variant=""
              >
                {visibilityOptions(language)[form.selectedVisibility]}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                <Dropdown.Item onClick={() => _updateForm({ selectedVisibility: "shared" })}>
                  {translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.VISIBILITY.SHARED")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => _updateForm({ selectedVisibility: "private" })}>
                  {translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.VISIBILITY.PRIVATE")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "GLOBAL.ORGANIZER")}</Form.Label>
            <Form.Control className="border-secondary" type="text" readOnly value={email} disabled />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.ANIMATORS")}</Form.Label>
            <MultiSelectDropdown
              mock={resources.animators.length === 0}
              btnWidth="100%"
              label={
                form.selectedAnimators.length > 0
                  ? `${form.selectedAnimators.length} ${translate(
                      language,
                      `PAGES.MEETINGS.MODAL.PLACEHOLDERS.${
                        form.selectedAnimators.length === 1 ? "ANIMATOR_SELECTED" : "ANIMATORS_SELECTED"
                      }`
                    )}`
                  : translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.ANIMATORS")
              }
              items={resources.animators}
              defaultSelectedItems={form.selectedAnimators}
              onConfirm={(selectedAnimators) => _updateForm({ selectedAnimators })}
              badgeItemProperty="email"
              displayItem={(item) => item.email}
              language={language}
              showAlwaysConfirm
              toggleClass={`text-truncate w-100 border-secondary ${
                form.selectedAnimators.length === 0 ? "text-muted" : ""
              }`}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.SERVICE_LINK_GENERATION")}</Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                className="w-100 d-flex justify-content-between align-items-center border-secondary"
                variant=""
              >
                {form.meetingLinkService === "zoom" &&
                  translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.ZOOM")}
                {form.meetingLinkService === "teams" &&
                  translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.TEAMS")}
                {form.meetingLinkService === null &&
                  translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.NONE")}
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100">
                <Dropdown.Item onClick={() => _updateForm({ meetingLinkService: "zoom" })}>
                  {translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.ZOOM")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => _updateForm({ meetingLinkService: "teams" })}>
                  {translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.TEAMS")}
                </Dropdown.Item>

                <Dropdown.Item onClick={() => _updateForm({ meetingLinkService: null })}>
                  {translate(language, "PAGES.MEETINGS.MODAL.OPTIONS.SERVICE_LINK_GENERATION.NONE")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          {form.meetingLinkService === null && (
            <Form.Group className="mt-3">
              <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.MEETING_LINK")}</Form.Label>
              <Form.Control
                className={`border-secondary ${!form.meetingLink ? "text-muted" : ""}`}
                type="text"
                placeholder={translate(language, "PAGES.MEETINGS.MODAL.PLACEHOLDERS.MEETING_LINK")}
                value={form.meetingLink}
                onChange={(event) => _updateForm({ meetingLink: event.target.value })}
              />
            </Form.Group>
          )}

          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "PAGES.MEETINGS.MODAL.LABELS.DESCRIPTION")}</Form.Label>
            <Form.Control
              className={`border-secondary ${!form.description ? "text-muted" : ""}`}
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(event) => _updateForm({ description: event.target.value })}
            />
          </Form.Group>
        </Form>

        <Modal.Footer>
          <DstButton
            variant="secondary"
            value={translate(language, "GLOBAL.CANCEL")}
            clickFunction={() => {
              setShowCreationModal(false);
              resetSelection();
            }}
          />
          <DstButton
            value={translate(language, "PAGES.MEETINGS.MODAL.BUTTONS.CONFIRM")}
            clickFunction={handleConfirm}
            disabled={!isFormValid}
            loading={loading.submit}
          />
        </Modal.Footer>
      </Popup>
      <DstAlert show={alert.show} onHide={_handleAlertClose} {...alert.props} />
    </React.Fragment>
  );
};

export default MeetingCreationModal;
